import { AxiosResponse } from 'axios';
import { mapFxCategorizationRule } from '@/lib/mappers';
import { supabase } from '@/lib/supabase';
import {
  asResponse,
  paginationRange,
  buildPaginatedResponse,
  throwIfError,
} from '@/lib/supabase-helpers';
import { ResponseWithPagination } from '@/types';
import { FxCategorizationRule } from '@/types/api/finance';
import { getActiveOrgId, applyOrgFilter } from '@/lib/activeOrg';

const SELECT_WITH_JOINS = '*, fx_vendors(*, fx_categories(*)), fx_categories(*)';

const financeRulesService = {
  async getAll({
    page,
    take,
  }: {
    page: number;
    take: number;
  }): Promise<AxiosResponse<ResponseWithPagination<FxCategorizationRule[]>>> {
    const [from, to] = paginationRange(page, take);
    let query = supabase
      .from('fx_categorization_rules')
      .select(SELECT_WITH_JOINS, { count: 'exact' })
      .order('priority', { ascending: false })
      .range(from, to);
    query = applyOrgFilter(query);
    const { data, error, count } = await query;

    if (error) throw error;
    return asResponse(
      buildPaginatedResponse((data ?? []).map(mapFxCategorizationRule), count ?? 0, page, take)
    );
  },

  async getAllActive(): Promise<AxiosResponse<FxCategorizationRule[]>> {
    let query = supabase
      .from('fx_categorization_rules')
      .select(SELECT_WITH_JOINS)
      .eq('is_active', true)
      .order('priority', { ascending: false });
    query = applyOrgFilter(query);
    const { data, error } = await query;

    if (error) throw error;
    return asResponse((data ?? []).map(mapFxCategorizationRule));
  },

  async create(payload: {
    name?: string;
    matchType: string;
    matchPattern: string;
    vendorId?: string;
    categoryId?: string;
    source?: string;
    priority?: number;
  }): Promise<AxiosResponse<FxCategorizationRule>> {
    const orgId = getActiveOrgId();
    const insert: Record<string, unknown> = {
      match_type: payload.matchType,
      match_pattern: payload.matchPattern,
      ...(orgId ? { organization_id: orgId } : {}),
    };
    if (payload.name !== undefined) insert.name = payload.name;
    if (payload.vendorId !== undefined) insert.vendor_id = payload.vendorId;
    if (payload.categoryId !== undefined) insert.category_id = payload.categoryId;
    if (payload.source !== undefined) insert.source = payload.source;
    if (payload.priority !== undefined) insert.priority = payload.priority;

    const result = await supabase
      .from('fx_categorization_rules')
      .insert(insert)
      .select(SELECT_WITH_JOINS)
      .single();

    return asResponse(mapFxCategorizationRule(throwIfError(result)));
  },

  async update(
    id: string,
    payload: {
      name?: string;
      matchType?: string;
      matchPattern?: string;
      vendorId?: string | null;
      categoryId?: string | null;
      priority?: number;
      isActive?: boolean;
    }
  ): Promise<AxiosResponse<FxCategorizationRule>> {
    const updates: Record<string, unknown> = {};
    if (payload.name !== undefined) updates.name = payload.name;
    if (payload.matchType !== undefined) updates.match_type = payload.matchType;
    if (payload.matchPattern !== undefined) updates.match_pattern = payload.matchPattern;
    if (payload.vendorId !== undefined) updates.vendor_id = payload.vendorId;
    if (payload.categoryId !== undefined) updates.category_id = payload.categoryId;
    if (payload.priority !== undefined) updates.priority = payload.priority;
    if (payload.isActive !== undefined) updates.is_active = payload.isActive;

    const result = await supabase
      .from('fx_categorization_rules')
      .update(updates)
      .eq('id', id)
      .select(SELECT_WITH_JOINS)
      .single();

    return asResponse(mapFxCategorizationRule(throwIfError(result)));
  },

  async delete(id: string): Promise<AxiosResponse<void>> {
    const { error } = await supabase.from('fx_categorization_rules').delete().eq('id', id);

    if (error) throw error;
    return asResponse(undefined as void);
  },

  async findByPattern(matchPattern: string): Promise<AxiosResponse<FxCategorizationRule[]>> {
    let query = supabase
      .from('fx_categorization_rules')
      .select(SELECT_WITH_JOINS)
      .eq('match_pattern', matchPattern);
    query = applyOrgFilter(query);
    const { data, error } = await query;

    if (error) throw error;
    return asResponse((data ?? []).map(mapFxCategorizationRule));
  },

  async incrementHitCount(id: string): Promise<void> {
    const { data } = await supabase
      .from('fx_categorization_rules')
      .select('hit_count')
      .eq('id', id)
      .single();

    await supabase
      .from('fx_categorization_rules')
      .update({ hit_count: (data?.hit_count ?? 0) + 1, last_hit_at: new Date().toISOString() })
      .eq('id', id);
  },

  async incrementOverrideCount(id: string): Promise<void> {
    const { data } = await supabase
      .from('fx_categorization_rules')
      .select('override_count')
      .eq('id', id)
      .single();

    await supabase
      .from('fx_categorization_rules')
      .update({
        override_count: (data?.override_count ?? 0) + 1,
        last_override_at: new Date().toISOString(),
      })
      .eq('id', id);
  },

  async getStaleRules(): Promise<FxCategorizationRule[]> {
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    let query = supabase
      .from('fx_categorization_rules')
      .select(SELECT_WITH_JOINS)
      .eq('is_active', true)
      .or(`last_hit_at.is.null,last_hit_at.lt.${ninetyDaysAgo.toISOString()}`);
    query = applyOrgFilter(query);
    const { data, error } = await query;

    if (error) throw error;
    return (data ?? []).map(mapFxCategorizationRule);
  },

  async pruneAndOptimize(): Promise<{ deactivated: number; boosted: number }> {
    let deactivated = 0;
    let boosted = 0;

    // Deactivate rules with accuracy < 50% (min 5 samples)
    let query = supabase
      .from('fx_categorization_rules')
      .select('id, hit_count, override_count, last_hit_at')
      .eq('is_active', true);
    query = applyOrgFilter(query);
    const { data: allRules, error } = await query;

    if (error) throw error;

    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    for (const rule of allRules ?? []) {
      const hits = rule.hit_count ?? 0;
      const overrides = rule.override_count ?? 0;
      const total = hits + overrides;
      const accuracy = total > 0 ? hits / total : 1;

      // Deactivate low-accuracy rules with enough samples
      if (total >= 5 && accuracy < 0.5) {
        await supabase
          .from('fx_categorization_rules')
          .update({ is_active: false })
          .eq('id', rule.id);
        deactivated += 1;
        continue;
      }

      // Deactivate stale rules (no hits in 90 days, at least 1 override)
      if (
        overrides > 0 &&
        (!rule.last_hit_at || new Date(rule.last_hit_at) < ninetyDaysAgo)
      ) {
        await supabase
          .from('fx_categorization_rules')
          .update({ is_active: false })
          .eq('id', rule.id);
        deactivated += 1;
        continue;
      }

      // Boost high-accuracy rules to priority 80
      if (total >= 5 && accuracy >= 0.9) {
        await supabase
          .from('fx_categorization_rules')
          .update({ priority: 80 })
          .eq('id', rule.id);
        boosted += 1;
      }
    }

    return { deactivated, boosted };
  },
};

export default financeRulesService;
